// # 영구 링크 — 한 줄을 가리키는 주소
//
// 주소는 **해시**에 산다(`#/c/7/42`). 경로가 아니라 해시인 이유: 이 앱은 데스크톱에서
// `file://` 로도 뜨고, 웹에서도 정적 파일로 서빙된다 — 서버 라우팅을 요구하지 않는 쪽이
// 어디서나 같게 동작한다. 해시는 새로 고침도 뒤로 가기도 공짜로 따라온다.

import type { ChannelId, Seq } from '@maru/protocol'

/** 지금 창 기준의 영구 링크. 해시만 갈아 끼운다. */
export function link_to(channel: ChannelId, seq: Seq, where?: Location): string {
  const loc = where ?? globalThis.location
  return `${loc.origin}${loc.pathname}${loc.search}#/c/${channel}/${seq}`
}

/** 해시를 읽어 좌표로. 모양이 아니면 `null` — 남이 붙인 해시에 반응하지 않는다. */
export function read_link(hash: string): { channel: ChannelId; seq: Seq } | null {
  const m = /^#\/c\/(\d+)\/(\d+)$/.exec(hash)
  if (m === null) return null
  const channel = Number(m[1])
  const seq = Number(m[2])
  if (!Number.isSafeInteger(channel) || !Number.isSafeInteger(seq)) return null
  if (channel <= 0 || seq <= 0) return null
  return { channel: channel as ChannelId, seq: seq as Seq }
}

/**
 * 환경설정의 자리 — `#/settings/{탭}`. 새로 고치면 설정이 닫히고 채널로 돌아가던 것(3차 F48)을
 * 되살리려고 적는다. 탭 이름이 맞는지는 설정 화면이 본다(모르는 탭이면 첫 탭).
 */
export function settings_hash(tab: string): string {
  return `#/settings/${tab}`
}

/** 해시가 환경설정 자리면 그 탭 이름, 아니면 `null`. 탭 없이 `#/settings` 면 빈 글자(첫 탭). */
export function read_settings(hash: string): string | null {
  const m = /^#\/settings(?:\/([a-z_]+))?$/.exec(hash)
  return m === null ? null : (m[1] ?? '')
}

/** 해시를 소리 없이 갈아 끼운다 — `hashchange` 가 안 나서 줄 뛰기가 깨어나지 않고, 뒤로 가기 이력도 안 쌓인다. */
export function put_hash(hash: string, where: Window | undefined = globalThis.window): void {
  if (where === undefined) return
  const { pathname, search } = where.location
  where.history.replaceState(where.history.state, '', `${pathname}${search}${hash}`)
}

/** 클립보드에 넣는다. 못 넣으면 `false` — 조용히 실패하고 부르는 쪽이 정한다. */
export async function copy_text(text: string): Promise<boolean> {
  try {
    await globalThis.navigator?.clipboard?.writeText(text)
    return true
  } catch {
    return false
  }
}

/** 우리가 쓰는 만큼의 저장소. `localStorage` 가 이 모양이다. */
interface Shelf {
  getItem(key: string): string | null
  setItem(key: string, value: string): void
}

/**
 * 이 기기에서 마지막으로 연 채널. 새로 고치면 늘 첫 채널로 돌아가 보던 곳을 다시 찾아야 했다(3차 F161).
 * 사람마다 따로 적는다(한 기기를 나눠 쓴다). 못 읽으면 `null` — 부르는 쪽이 첫 채널을 고른다.
 */
export function last_open(shelf: Shelf | undefined, me: number): ChannelId | null {
  try {
    const id = Number(shelf?.getItem(`maru.open.${me}`) ?? '')
    return Number.isSafeInteger(id) && id > 0 ? (id as ChannelId) : null
  } catch {
    return null
  }
}

/** 연 채널을 적는다. 저장이 막혀도(비공개 모드) 조용히 넘어간다 — 편의지 약속이 아니다. */
export function keep_open(shelf: Shelf | undefined, me: number, channel: ChannelId): void {
  try {
    shelf?.setItem(`maru.open.${me}`, String(channel))
  } catch {}
}
